import React, { useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Menu, MenuItem, Collapse, ListItemText } from '@mui/material';
import {
  Person,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { Button, ConfirmationModal } from '../';
import * as translations from './intl';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import { apiService } from '../../services';
import { useTranslation } from '../../shared/hooks';
import { getCookie, setCookie } from '../../shared/helpers/cookies';

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    border: '1px solid #d3d4d5',
    minWidth: 175,
    maxWidth: 275,
  },
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:focus': {},
  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
    fontFamily: 'Source Sans Pro',
    fontWeight: 700,
    color: theme.palette.secondary.main,
  },
}));

const ButtonOpen = styled(Button)({
  minWidth: 175,
  maxWidth: 275,
  justifyContent: 'space-around',
});

// --- Main Component ---

export default function UserMenu({ mobile = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { locale } = useTranslation({
    translationsFile: translations,
  });
  const { userData, logoutAmos, setIsLoading, isLogged } =
    useContext(GeneralContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMobileCollapse, setOpenMobileCollapse] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logoutAmos();
    setAnchorEl(null);
    setOpenMobileCollapse(false);

    const brand = getCookie('brand');
    const policyNumber = getCookie('policyNumber');

    if (brand && policyNumber) {
      if (brand === 'CoverForYou') {
        setCookie(
          'brandCookieURL',
          import.meta.env.VITE_WWIIS_URL1 + `${policyNumber}`
        );
      } else if (brand === 'CedarTree') {
        setCookie(
          'brandCookieURL',
          import.meta.env.VITE_WWIIS_URL2 + `${policyNumber}`
        );
      } else if (brand === 'Outbacker') {
        setCookie(
          'brandCookieURL',
          import.meta.env.VITE_WWIIS_URL3 + `${policyNumber}`
        );
      } else if (brand === 'InsureAndEscape') {
        setCookie(
          'brandCookieURL',
          import.meta.env.VITE_WWIIS_URL4 + `${policyNumber}`
        );
      }
    }

    navigate('/logoff');

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      zone: 'profile',
      element_clicked: 'logout',
      event: 'navigation',
    });
  };

  const handleChangePassword = async () => {
    try {
      setIsLoading(true);
      await apiService.user.requestNewPassword({ email: userData.email });
      setOpen(true);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      zone: 'profile',
      element_clicked: 'change password',
      event: 'navigation',
    });
  };

  const { CHANGE_PASSWORD, LOGOUT, PASSWORD_RESET_TEXT } = locale;

  const closeModal = () => setOpen(false);

  const handleCollapse = () => setOpenMobileCollapse(!openMobileCollapse);

  const iconButton = useMemo(
    () => (mobile ? openMobileCollapse : anchorEl),
    [mobile, openMobileCollapse, anchorEl]
  );

  return (
    <div>
      <ConfirmationModal
        open={open}
        handleClose={closeModal}
        handleAction={closeModal}
        title={CHANGE_PASSWORD}
        text={PASSWORD_RESET_TEXT}
        okButton
      />
      {isLogged && userData && (
        <>
          <ButtonOpen
            text={`${userData.given_name} ${userData.family_name}`}
            variant="outlined"
            color="primary"
            icon={<Person />}
            endIcon={iconButton ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            onClick={mobile ? handleCollapse : handleClick}
          />
          {!mobile && (
            <StyledMenu
              id="customized-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              // anchorOrigin={{
              //   vertical: 'bottom',
              //   horizontal: 'center',
              // }}
              // transformOrigin={{
              //   vertical: 'top',
              //   horizontal: 'center',
              // }}
            >
              <StyledMenuItem onClick={handleChangePassword}>
                <ListItemText primary={CHANGE_PASSWORD} />
              </StyledMenuItem>
              <StyledMenuItem onClick={handleLogout}>
                <ListItemText primary={LOGOUT} />
              </StyledMenuItem>
            </StyledMenu>
          )}
          {mobile && (
            <Collapse in={openMobileCollapse} unmountOnExit>
              <StyledMenuItem onClick={handleChangePassword}>
                <ListItemText primary={CHANGE_PASSWORD} />
              </StyledMenuItem>
              <StyledMenuItem onClick={handleLogout}>
                <ListItemText primary={LOGOUT} />
              </StyledMenuItem>
            </Collapse>
          )}
        </>
      )}
    </div>
  );
}
